/**
 * Custom WebGL Overlay for rendering GeoJSON on Google Maps (Legacy API)
 * Uses Mercator-projected world coordinates and a simple affine matrix
 * to avoid per-frame re-projection while staying pixel-accurate at all zooms.
 *
 * World coordinates use Google's standard: world size = 256 at zoom 0,
 * x grows east, y grows south. We store geometry in world coords once,
 * then each frame we compute a matrix that maps world coords → clip space
 * based on the current map viewport.
 */

export class CanvasGeoJsonOverlay extends google.maps.OverlayView {
  constructor(map, geojson, style = {}) {
    super();

    this.map = map;
    this.geojson = geojson;
    this.style = {
      fillColor: style.fillColor ?? '#3388ff',
      markerFillColor: style.markerFillColor ?? style.fillColor ?? '#3388ff',
      strokeColor: style.strokeColor ?? '#3388ff',
      strokeWeight: style.strokeWeight ?? 2,
      fillOpacity: style.fillOpacity ?? 0.2,
      strokeOpacity: style.strokeOpacity ?? 1,
      icon: style.icon ?? null,
      visible: style.visible !== false,
      zIndex: style.zIndex ?? 1
    }

    this.canvas = null;
    this.gl = null;
    this.bounds = null;
    this.features = [];
    this.programs = {};
    this.buffers = {};
    this.locations = {};          // cached uniform/attribute locations
    this.geometryData = { polygons: [], lineStrips: [], lines: [], points: [] };
    this.needsGeometryUpdate = true;

    // Pre-allocated matrix to avoid per-frame Float32Array allocation
    this._matrix = new Float32Array(16);

    // Pre-computed color values (updated in _cacheColors)
    this._colors = { fill: null, stroke: null, marker: null };
    this._cacheColors();

    // Track last canvas style values to avoid redundant style writes
    this._lastCanvasStyle = { left: null, top: null, width: null, height: null };
    this._lastCanvasSize = { w: 0, h: 0 };

    this.parseGeoJson(geojson);
    this.setMap(map);
  }

  // ── Google Maps OverlayView lifecycle ──────────────────────────────

  onAdd() {
    const panes = this.getPanes();

    this.canvas = document.createElement('canvas');
    this.canvas.style.position = 'absolute';
    this.canvas.style.pointerEvents = 'none';
    this.canvas.style.zIndex = this.style.zIndex;
    this.canvas.id = 'geojson-webgl-canvas';

    panes.overlayLayer.appendChild(this.canvas);

    // Skip MSAA on high-DPI — the supersampling from devicePixelRatio
    // already provides equivalent quality, and MSAA doubles fill cost.
    const dpr = window.devicePixelRatio || 1;
    const aa = dpr <= 1;

    this.gl = this.canvas.getContext('webgl', {
      alpha: true,
      antialias: aa,
      preserveDrawingBuffer: false
    }) || this.canvas.getContext('experimental-webgl', {
      alpha: true,
      antialias: aa,
      preserveDrawingBuffer: false
    });

    if (!this.gl) {
      console.error('WebGL not supported');
      return;
    }

    this.initWebGL();

    setTimeout(() => {
      this.prepareGeometryData();
      this.draw();
    }, 0);
  }

  onRemove() {
    if (this.gl) {
      Object.values(this.programs).forEach(p => { if (p) this.gl.deleteProgram(p); });
      Object.values(this.buffers).forEach(b => { if (b) this.gl.deleteBuffer(b); });
    }
    if (this.canvas && this.canvas.parentNode) {
      this.canvas.parentNode.removeChild(this.canvas);
    }
    this.canvas = null;
    this.gl = null;
    this.locations = {};
    this._lastCanvasStyle = { left: null, top: null, width: null, height: null };
    this._lastCanvasSize = { w: 0, h: 0 };
  }

  // ── Mercator helpers ───────────────────────────────────────────────

  /**
   * Convert lng/lat to Google-style world coordinates (256-unit world at zoom 0).
   * x ∈ [0, 256], y ∈ [0, 256].  This is the standard Web Mercator formula
   * that Google Maps uses internally.
   */
  static lngLatToWorld(lng, lat) {
    const siny = Math.sin((lat * Math.PI) / 180);
    // Clamp to avoid Infinity at the poles
    const clampedSiny = Math.max(-0.9999, Math.min(0.9999, siny));
    return {
      x: 128 + (lng * (256 / 360)),
      y: 128 - (256 / (2 * Math.PI)) * Math.log((1 + clampedSiny) / (1 - clampedSiny)) / 2
    };
  }

  // ── WebGL setup ────────────────────────────────────────────────────

  initWebGL() {
    const gl = this.gl;
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    const vtx = this.matrixVertexShaderSource();
    const frag = this.fragmentShaderSource();
    this.programs.polygon = this.createProgram(vtx, frag);
    this.programs.line = this.createProgram(this.lineVertexShaderSource(), frag);
    this.programs.point = this.createProgram(
      this.pointMatrixVertexShaderSource(),
      this.pointFragmentShaderSource()
    );

    // Cache all uniform/attribute locations once — avoids string lookups every frame
    this._cacheLocations();
  }

  /**
   * Cache GL uniform and attribute locations for all programs.
   * Called once after shader compilation. Eliminates per-frame
   * getAttribLocation / getUniformLocation overhead.
   */
  _cacheLocations() {
    const gl = this.gl;

    if (this.programs.polygon) {
      const p = this.programs.polygon;
      this.locations.polygon = {
        a_position: gl.getAttribLocation(p, 'a_position'),
        u_matrix: gl.getUniformLocation(p, 'u_matrix'),
        u_color: gl.getUniformLocation(p, 'u_color')
      };
    }

    if (this.programs.line) {
      const p = this.programs.line;
      this.locations.line = {
        a_position: gl.getAttribLocation(p, 'a_position'),
        a_normal: gl.getAttribLocation(p, 'a_normal'),
        a_side: gl.getAttribLocation(p, 'a_side'),
        u_matrix: gl.getUniformLocation(p, 'u_matrix'),
        u_width: gl.getUniformLocation(p, 'u_width'),
        u_resolution: gl.getUniformLocation(p, 'u_resolution'),
        u_color: gl.getUniformLocation(p, 'u_color')
      };
    }

    if (this.programs.point) {
      const p = this.programs.point;
      this.locations.point = {
        a_position: gl.getAttribLocation(p, 'a_position'),
        u_matrix: gl.getUniformLocation(p, 'u_matrix'),
        u_color: gl.getUniformLocation(p, 'u_color'),
        u_pointSize: gl.getUniformLocation(p, 'u_pointSize'),
        u_scale: gl.getUniformLocation(p, 'u_scale')
      };
    }
  }

  /**
   * Pre-compute RGBA color values from hex style properties.
   * Called once on construction and whenever style changes.
   */
  _cacheColors() {
    const markerColor = this.style.markerFillColor || this.style.fillColor;
    this._colors.fill = this.hexToRgba(this.style.fillColor, this.style.fillOpacity);
    this._colors.stroke = this.hexToRgba(this.style.strokeColor, this.style.strokeOpacity);
    this._colors.marker = this.hexToRgba(markerColor, 1.0);
  }

  matrixVertexShaderSource() {
    return `
      attribute vec2 a_position;
      uniform mat4 u_matrix;
      void main() {
        gl_Position = u_matrix * vec4(a_position, 0, 1);
      }
    `;
  }

  /**
   * Vertex shader for thick lines rendered as triangle strips.
   * Each vertex carries the world-space position of the line point
   * plus a miter/normal vector and a sign (+1 or -1) indicating
   * which side of the line this vertex sits on.
   * The extrusion is done in clip space so the width is in pixels.
   */
  lineVertexShaderSource() {
    return `
      attribute vec2 a_position;
      attribute vec2 a_normal;
      attribute float a_side;
      uniform mat4 u_matrix;
      uniform float u_width;
      uniform vec2 u_resolution;
      void main() {
        vec4 clip = u_matrix * vec4(a_position, 0, 1);
        vec4 clipN = u_matrix * vec4(a_position + a_normal, 0, 1);
        vec2 dir = clipN.xy / clipN.w - clip.xy / clip.w;
        float len = length(dir);
        if (len > 0.0) dir = dir / len;
        vec2 offset = dir * u_width * a_side / u_resolution;
        gl_Position = vec4(clip.xy + offset * clip.w, clip.zw);
      }
    `;
  }

  pointMatrixVertexShaderSource() {
      return `
        attribute vec2 a_position;
        uniform mat4 u_matrix;
        uniform float u_pointSize;
        uniform float u_scale;
        void main() {
          gl_Position = u_matrix * vec4(a_position, 0, 1);
          gl_PointSize = u_pointSize * u_scale;
        }
      `;
    }

  fragmentShaderSource() {
    return `
      precision mediump float;
      uniform vec4 u_color;
      void main() {
        gl_FragColor = u_color;
      }
    `;
  }

  pointFragmentShaderSource() {
      return `
        precision mediump float;
        uniform vec4 u_color;
        void main() {
          vec2 uv = gl_PointCoord;
          // Remap to centered coords: (-0.5 .. 0.5)
          vec2 p = uv - vec2(0.5);

          // --- Teardrop geometry ---
          // Circle head centered above midpoint
          vec2 center = vec2(0.0, -0.10);
          float R = 0.28;
          // Tip of the pin at the bottom
          vec2 tip = vec2(0.0, 0.46);

          // Tangent point angle: where the straight edges meet the circle.
          // sin(a) = R / dist(center, tip)
          float d = length(tip - center);
          float sinA = R / d;
          float cosA = sqrt(1.0 - sinA * sinA);

          // Direction from center to tip
          vec2 dir = normalize(tip - center);
          // Perpendicular
          vec2 perp = vec2(-dir.y, dir.x);

          // The two tangent points on the circle
          vec2 tL = center + R * ( dir * sinA + perp * cosA);
          vec2 tR = center + R * ( dir * sinA - perp * cosA);

          // Shape test
          float distC = length(p - center);
          bool inCircle = distC <= R;

          // Below the tangent points: test if inside the triangle (tL, tR, tip)
          float edgeL = (p.x - tL.x) * (tip.y - tL.y) - (p.y - tL.y) * (tip.x - tL.x);
          float edgeR = (p.x - tip.x) * (tR.y - tip.y) - (p.y - tip.y) * (tR.x - tip.x);

          // How far along the center→tip axis is this pixel relative to the tangent line?
          float tangentDot = dot(p - center, dir);
          bool belowTangent = tangentDot > R * sinA;

          // Above tangent chord: use circle. Below: use left/right edge tests only.
          // This clips the circle's lower bulge so it can't protrude past the tangent lines.
          bool inUpper = inCircle && !belowTangent;
          bool inLower = belowTangent && edgeL >= 0.0 && edgeR >= 0.0 && p.y <= tip.y;

          bool inShape = inUpper || inLower;
          if (!inShape) discard;

          // --- Border ---
          float bw = 0.03;
          bool inBorder = false;

          // Circle border (upper arc only, above tangent chord)
          if (inUpper && distC > R - bw) {
            inBorder = true;
          }

          // Tail edge borders: distance to left and right edge line segments
          if (inLower) {
            vec2 ab = tip - tL;
            float t1 = clamp(dot(p - tL, ab) / dot(ab, ab), 0.0, 1.0);
            float dL = length(p - tL - t1 * ab);

            vec2 cd = tip - tR;
            float t2 = clamp(dot(p - tR, cd) / dot(cd, cd), 0.0, 1.0);
            float dR = length(p - tR - t2 * cd);

            if (min(dL, dR) < bw) inBorder = true;
          }

          // --- Compose color ---
          vec3 borderColor = u_color.rgb * 0.45;
          vec3 finalColor = u_color.rgb;
          float finalAlpha = u_color.a;

          if (inBorder) {
            finalColor = borderColor;
          } else {
            // Subtle radial shading for depth
            float shade = 1.0 - 0.22 * smoothstep(0.0, R, distC);
            finalColor = u_color.rgb * shade;
          }

          // White center dot
          if (length(p - center) <= 0.09) {
            finalColor = vec3(1.0);
          }

          gl_FragColor = vec4(finalColor, finalAlpha);
        }
      `;
    }

  createProgram(vertexSource, fragmentSource) {
    const gl = this.gl;
    const vs = this.createShader(gl.VERTEX_SHADER, vertexSource);
    const fs = this.createShader(gl.FRAGMENT_SHADER, fragmentSource);
    const program = gl.createProgram();
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error('Program link error:', gl.getProgramInfoLog(program));
      return null;
    }
    return program;
  }

  createShader(type, source) {
    const gl = this.gl;
    const shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      console.error('Shader compile error:', gl.getShaderInfoLog(shader));
      gl.deleteShader(shader);
      return null;
    }
    return shader;
  }

  // ── Geometry preparation (runs once) ───────────────────────────────

  prepareGeometryData() {
    this.geometryData = { polygons: [], lineStrips: [], lines: [], points: [] };

    // Compute a reference world-x for the geometry centroid so we can
    // normalise all longitudes relative to it.  This avoids the
    // antimeridian / prime-meridian wrap problem: features near lng 180
    // won't suddenly jump 256 world-units away from features near lng -180.
    this._refWorldX = this._computeReferenceWorldX();

    this.features.forEach(f => this.processFeatureGeometry(f.geometry));
    this.uploadGeometryBuffers();
    this.needsGeometryUpdate = false;
  }

  /**
   * Compute the median world-x of all geometry coordinates.
   * Used as the "anchor" so that world-x values are continuous
   * even when geometry straddles the antimeridian.
   */
  _computeReferenceWorldX() {
    const xs = [];
    this.features.forEach(f => {
      this.forEachLatLng(f.geometry, (_lat, lng) => {
        xs.push(128 + (lng * (256 / 360)));
      });
    });
    if (xs.length === 0) return 128;
    xs.sort((a, b) => a - b);
    return xs[Math.floor(xs.length / 2)];
  }

  /**
   * Convert a lng/lat pair to world coords, keeping world-x continuous
   * relative to _refWorldX (handles antimeridian wrap).
   */
  _toWorld(lng, lat) {
    const w = CanvasGeoJsonOverlay.lngLatToWorld(lng, lat);
    // Shift x so it's within ±128 of the reference
    while (w.x - this._refWorldX > 128) w.x -= 256;
    while (w.x - this._refWorldX < -128) w.x += 256;
    return w;
  }

  processFeatureGeometry(geometry) {
    switch (geometry.type) {
      case 'Point': {
        const w = this._toWorld(geometry.coordinates[0], geometry.coordinates[1]);
        this.geometryData.points.push(w.x, w.y);
        break;
      }
      case 'MultiPoint':
        geometry.coordinates.forEach(c => {
          const w = this._toWorld(c[0], c[1]);
          this.geometryData.points.push(w.x, w.y);
        });
        break;
      case 'LineString':
        this.addLineString(geometry.coordinates);
        break;
      case 'MultiLineString':
        geometry.coordinates.forEach(c => this.addLineString(c));
        break;
      case 'Polygon':
        this.addPolygon(geometry.coordinates);
        break;
      case 'MultiPolygon':
        geometry.coordinates.forEach(c => this.addPolygon(c));
        break;
    }
  }

  addLineString(coords) {
    const strip = [];
    for (let i = 0; i < coords.length; i++) {
      const w = this._toWorld(coords[i][0], coords[i][1]);
      strip.push(w.x, w.y);
    }
    if (strip.length >= 4) { // at least 2 points
      this.geometryData.lineStrips.push(strip);
    }
  }

  /**
   * Build thick-line triangle-strip geometry from raw polyline strips.
   * For each segment we compute a perpendicular (miter) normal in world
   * coords and emit two vertices on either side.  The vertex shader
   * will project these and extrude by the pixel half-width.
   *
   * Output per vertex: [x, y, nx, ny, side]
   *   x,y   = world position of the line point
   *   nx,ny = perpendicular normal in world coords (unit-ish)
   *   side  = +1 or -1 (which side of the line)
   */
  buildThickLineGeometry() {
    const positions = [];
    const normals = [];
    const sides = [];
    const indexData = [];
    let vertexOffset = 0;

    for (const strip of this.geometryData.lineStrips) {
      const n = strip.length / 2; // number of points
      if (n < 2) continue;

      // Compute miter normals for each point
      const miterNormals = [];
      for (let i = 0; i < n; i++) {
        const x = strip[i * 2];
        const y = strip[i * 2 + 1];

        let nx, ny;
        if (i === 0) {
          // First point: use the direction of the first segment
          const dx = strip[2] - x;
          const dy = strip[3] - y;
          const len = Math.sqrt(dx * dx + dy * dy) || 1;
          nx = -dy / len;
          ny = dx / len;
        } else if (i === n - 1) {
          // Last point: use the direction of the last segment
          const dx = x - strip[(i - 1) * 2];
          const dy = y - strip[(i - 1) * 2 + 1];
          const len = Math.sqrt(dx * dx + dy * dy) || 1;
          nx = -dy / len;
          ny = dx / len;
        } else {
          // Interior point: average the normals of adjacent segments (miter)
          const dx0 = x - strip[(i - 1) * 2];
          const dy0 = y - strip[(i - 1) * 2 + 1];
          const len0 = Math.sqrt(dx0 * dx0 + dy0 * dy0) || 1;
          const nx0 = -dy0 / len0;
          const ny0 = dx0 / len0;

          const dx1 = strip[(i + 1) * 2] - x;
          const dy1 = strip[(i + 1) * 2 + 1] - y;
          const len1 = Math.sqrt(dx1 * dx1 + dy1 * dy1) || 1;
          const nx1 = -dy1 / len1;
          const ny1 = dx1 / len1;

          nx = (nx0 + nx1) * 0.5;
          ny = (ny0 + ny1) * 0.5;
          // Scale miter to maintain consistent width
          const dot = nx * nx0 + ny * ny0;
          if (dot > 0.1) {
            nx /= dot;
            ny /= dot;
          }
          // Clamp miter length to avoid spikes at sharp angles
          const miterLen = Math.sqrt(nx * nx + ny * ny);
          if (miterLen > 3.0) {
            nx = (nx / miterLen) * 3.0;
            ny = (ny / miterLen) * 3.0;
          }
        }
        miterNormals.push(nx, ny);
      }

      // Emit two vertices per point (one on each side)
      for (let i = 0; i < n; i++) {
        const x = strip[i * 2];
        const y = strip[i * 2 + 1];
        const mnx = miterNormals[i * 2];
        const mny = miterNormals[i * 2 + 1];

        // +side vertex
        positions.push(x, y);
        normals.push(mnx, mny);
        sides.push(1);

        // -side vertex
        positions.push(x, y);
        normals.push(mnx, mny);
        sides.push(-1);
      }

      // Build triangle-strip indices as indexed triangles
      for (let i = 0; i < n - 1; i++) {
        const base = vertexOffset + i * 2;
        // Two triangles per segment
        indexData.push(base, base + 1, base + 2);
        indexData.push(base + 1, base + 3, base + 2);
      }

      vertexOffset += n * 2;
    }

    this.geometryData.lines = {
      positions: new Float32Array(positions),
      normals: new Float32Array(normals),
      sides: new Float32Array(sides),
      indices: new Uint32Array(indexData),
      vertexCount: positions.length / 2,
      indexCount: indexData.length
    };
  }

  addPolygon(coords) {
    if (!coords || coords.length === 0) return;
    const outerRing = coords[0];
    if (outerRing.length < 3) return;

    // Triangulate for fill
    const triangles = this.triangulatePolygon(outerRing);
    this.geometryData.polygons.push(...triangles);

    // Add outline as a line strip so the polygon stroke is visible
    this.addLineString(outerRing);
  }

  triangulatePolygon(ring) {
    const points = ring.map(c => {
      const w = this._toWorld(c[0], c[1]);
      return { x: w.x, y: w.y };
    });

    // Remove duplicate closing vertex if present (GeoJSON rings repeat the first point)
    if (points.length > 1) {
      const first = points[0];
      const last = points[points.length - 1];
      if (Math.abs(first.x - last.x) < 1e-10 && Math.abs(first.y - last.y) < 1e-10) {
        points.pop();
      }
    }

    if (points.length < 3) return [];

    // Determine winding order of the projected ring using signed area.
    // In world coords y grows south, so GeoJSON CCW rings become CW here.
    const windingSign = this._signedArea(points);

    const triangles = [];
    const indices = points.map((_, i) => i);

    while (indices.length > 3) {
      let earFound = false;
      for (let i = 0; i < indices.length; i++) {
        const prev = indices[(i - 1 + indices.length) % indices.length];
        const curr = indices[i];
        const next = indices[(i + 1) % indices.length];
        if (this.isEar(points, indices, prev, curr, next, windingSign)) {
          triangles.push(
            points[prev].x, points[prev].y,
            points[curr].x, points[curr].y,
            points[next].x, points[next].y
          );
          indices.splice(i, 1);
          earFound = true;
          break;
        }
      }
      if (!earFound) break;
    }

    if (indices.length === 3) {
      triangles.push(
        points[indices[0]].x, points[indices[0]].y,
        points[indices[1]].x, points[indices[1]].y,
        points[indices[2]].x, points[indices[2]].y
      );
    }
    return triangles;
  }

  /**
   * Compute the signed area of a polygon ring (2× actual area).
   * Positive = CCW in standard math coords (y-up), negative = CW.
   * In world coords (y-down), the sign is flipped.
   */
  _signedArea(points) {
    let area = 0;
    for (let i = 0, n = points.length; i < n; i++) {
      const j = (i + 1) % n;
      area += (points[j].x - points[i].x) * (points[j].y + points[i].y);
    }
    return area;
  }

  uploadGeometryBuffers() {
    const gl = this.gl;
    if (this.geometryData.polygons.length > 0) {
      this.buffers.polygons = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.polygons);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(this.geometryData.polygons), gl.STATIC_DRAW);
    }
    if (this.geometryData.lineStrips.length > 0) {
      this.buildThickLineGeometry();
      const ld = this.geometryData.lines;
      if (ld.indexCount > 0) {
        this.buffers.linePositions = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.linePositions);
        gl.bufferData(gl.ARRAY_BUFFER, ld.positions, gl.STATIC_DRAW);

        this.buffers.lineNormals = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.lineNormals);
        gl.bufferData(gl.ARRAY_BUFFER, ld.normals, gl.STATIC_DRAW);

        this.buffers.lineSides = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.lineSides);
        gl.bufferData(gl.ARRAY_BUFFER, ld.sides, gl.STATIC_DRAW);

        // Check if we need 32-bit indices or can use 16-bit
        const ext = gl.getExtension('OES_element_index_uint');
        if (ext || ld.vertexCount <= 65535) {
          this.buffers.lineIndices = gl.createBuffer();
          gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.buffers.lineIndices);
          if (ext) {
            gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, ld.indices, gl.STATIC_DRAW);
            this._lineIndexType = gl.UNSIGNED_INT;
          } else {
            gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(ld.indices), gl.STATIC_DRAW);
            this._lineIndexType = gl.UNSIGNED_SHORT;
          }
        }
      }
    }
    if (this.geometryData.points.length > 0) {
      this.buffers.points = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.points);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(this.geometryData.points), gl.STATIC_DRAW);
    }
  }

  // ── Drawing (runs every frame) ─────────────────────────────────────

  draw() {
    if (!this.style.visible || !this.gl) return;

    const gl = this.gl;
    const projection = this.getProjection();
    if (!projection) return;

    const bounds = this.map.getBounds();
    if (!bounds) return;

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    // Get div-pixel positions for the viewport corners.
    // These are in the overlay's coordinate system (can be large / negative).
    const nePixel = projection.fromLatLngToDivPixel(ne);
    const swPixel = projection.fromLatLngToDivPixel(sw);

    // Handle the prime-meridian / antimeridian wrap:
    // When the map wraps, swPixel.x can be > nePixel.x.
    // We always want width > 0, so take the absolute span and
    // use Math.min for left.
    const left = Math.min(swPixel.x, nePixel.x);
    const top = Math.min(swPixel.y, nePixel.y);   // nePixel.y is north (smaller)
    const width = Math.abs(nePixel.x - swPixel.x);
    const height = Math.abs(swPixel.y - nePixel.y);

    if (width <= 0 || height <= 0) return;

    // Only write style properties when they actually change — avoids layout thrashing
    const ls = this._lastCanvasStyle;
    if (ls.left !== left || ls.top !== top || ls.width !== width || ls.height !== height) {
      this.canvas.style.left = left + 'px';
      this.canvas.style.top = top + 'px';
      this.canvas.style.width = width + 'px';
      this.canvas.style.height = height + 'px';
      ls.left = left;
      ls.top = top;
      ls.width = width;
      ls.height = height;
    }

    const dpr = window.devicePixelRatio || 1;
    const bufW = Math.round(width * dpr);
    const bufH = Math.round(height * dpr);

    if (this._lastCanvasSize.w !== bufW || this._lastCanvasSize.h !== bufH) {
      this.canvas.width = bufW;
      this.canvas.height = bufH;
      this._lastCanvasSize.w = bufW;
      this._lastCanvasSize.h = bufH;
    }
    gl.viewport(0, 0, bufW, bufH);

    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);

    // Build the world-coord → clip-space matrix (reuses pre-allocated Float32Array)
    const matrix = this.calculateTransformMatrix(projection, left, top, width, height, ne, sw, nePixel, swPixel);

    if (this.buffers.lineIndices && this.geometryData.lines.indexCount > 0) {
      this.drawLines(matrix);
    }
    if (this.buffers.polygons && this.geometryData.polygons.length > 0) {
      this.drawPolygons(matrix);
    }
    if (this.buffers.points && this.geometryData.points.length > 0) {
      this.drawPoints(matrix);
    }
  }

  /**
   * Build a 4×4 matrix that maps world coordinates → WebGL clip space.
   *
   * Strategy:
   *   1. Pick two reference lat/lngs (the viewport corners).
   *   2. Compute their world coords AND their div-pixel positions.
   *   3. Derive the affine mapping:  world → div-pixel → canvas-pixel → clip.
   *
   * Because both world coords and div-pixels are Mercator-based, the
   * mapping between them is a simple scale + translate (no non-linearity).
   * This is exact at every zoom level.
   */
  calculateTransformMatrix(_projection, canvasLeft, canvasTop, canvasWidth, canvasHeight, ne, sw, pNE, pSW) {
    // World coords of the two reference points
    const wSW = CanvasGeoJsonOverlay.lngLatToWorld(sw.lng(), sw.lat());
    const wNE = CanvasGeoJsonOverlay.lngLatToWorld(ne.lng(), ne.lat());

    // Handle antimeridian: keep wNE.x > wSW.x by shifting if needed
    if (wNE.x < wSW.x) wNE.x += 256;

    // Align reference world-x values with the geometry's reference frame
    // so that the geometry's _refWorldX sits between wSW.x and wNE.x.
    if (this._refWorldX !== undefined) {
      while (wSW.x - this._refWorldX > 128) { wSW.x -= 256; wNE.x -= 256; }
      while (wSW.x - this._refWorldX < -128) { wSW.x += 256; wNE.x += 256; }
      // After aligning SW, make sure NE is still to the right
      if (wNE.x < wSW.x) wNE.x += 256;
    }

    // Div-pixel positions already passed in from draw() — no redundant API calls

    // world → div-pixel is affine:  pixel = world * scale + offset
    // Solve for scale and offset from the two known pairs.
    const dwx = wNE.x - wSW.x;
    const dwy = wNE.y - wSW.y;
    const dpx = pNE.x - pSW.x;
    const dpy = pNE.y - pSW.y;

    // Avoid division by zero (degenerate viewport)
    if (Math.abs(dwx) < 1e-10 || Math.abs(dwy) < 1e-10) {
      return new Float32Array(16); // identity-ish, nothing will render meaningfully
    }

    const sx = dpx / dwx;  // div-pixels per world-unit, x
    const sy = dpy / dwy;  // div-pixels per world-unit, y

    const ox = pSW.x - wSW.x * sx;  // div-pixel offset x
    const oy = pSW.y - wSW.y * sy;  // div-pixel offset y

    // div-pixel → canvas-pixel: subtract canvas origin
    // canvas-pixel → clip:  x_clip = (canvasPx / canvasWidth) * 2 - 1
    //                        y_clip = 1 - (canvasPy / canvasHeight) * 2  (flip y)

    const cx = 2.0 / canvasWidth;
    const cy = -2.0 / canvasHeight;

    // Combined: clip = world * (s * c) + (o - canvasOrigin) * c + bias
    const totalSx = sx * cx;
    const totalSy = sy * cy;
    const totalOx = (ox - canvasLeft) * cx - 1.0;
    const totalOy = (oy - canvasTop) * cy + 1.0;

    // Reuse pre-allocated column-major 4×4 matrix
    const m = this._matrix;
    m[0]  = totalSx;   // col 0
    m[1]  = 0;
    m[2]  = 0;
    m[3]  = 0;
    m[4]  = 0;          // col 1
    m[5]  = totalSy;
    m[6]  = 0;
    m[7]  = 0;
    m[8]  = 0;          // col 2
    m[9]  = 0;
    m[10] = 1;
    m[11] = 0;
    m[12] = totalOx;    // col 3
    m[13] = totalOy;
    m[14] = 0;
    m[15] = 1;

    return m;
  }

  // ── Draw calls ─────────────────────────────────────────────────────

  drawLines(matrix) {
    const gl = this.gl;
    const program = this.programs.line;
    if (!program) return;

    const ld = this.geometryData.lines;
    if (!ld || ld.indexCount === 0) return;

    const loc = this.locations.line;
    gl.useProgram(program);

    // Bind position attribute
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.linePositions);
    gl.enableVertexAttribArray(loc.a_position);
    gl.vertexAttribPointer(loc.a_position, 2, gl.FLOAT, false, 0, 0);

    // Bind normal attribute
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.lineNormals);
    gl.enableVertexAttribArray(loc.a_normal);
    gl.vertexAttribPointer(loc.a_normal, 2, gl.FLOAT, false, 0, 0);

    // Bind side attribute
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.lineSides);
    gl.enableVertexAttribArray(loc.a_side);
    gl.vertexAttribPointer(loc.a_side, 1, gl.FLOAT, false, 0, 0);

    // Uniforms
    gl.uniformMatrix4fv(loc.u_matrix, false, matrix);

    const dpr = window.devicePixelRatio || 1;
    const halfWidth = (this.style.strokeWeight * dpr) / 2.0;
    gl.uniform1f(loc.u_width, halfWidth);
    gl.uniform2f(loc.u_resolution, this.canvas.width, this.canvas.height);

    const c = this._colors.stroke;
    gl.uniform4f(loc.u_color, c.r, c.g, c.b, c.a);

    // Draw indexed triangles
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.buffers.lineIndices);
    gl.drawElements(gl.TRIANGLES, ld.indexCount, this._lineIndexType, 0);

    // Clean up
    gl.disableVertexAttribArray(loc.a_normal);
    gl.disableVertexAttribArray(loc.a_side);
  }

  drawPolygons(matrix) {
    const gl = this.gl;
    const program = this.programs.polygon;
    if (!program) return;

    const loc = this.locations.polygon;
    gl.useProgram(program);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.polygons);

    gl.enableVertexAttribArray(loc.a_position);
    gl.vertexAttribPointer(loc.a_position, 2, gl.FLOAT, false, 0, 0);

    gl.uniformMatrix4fv(loc.u_matrix, false, matrix);

    const c = this._colors.fill;
    gl.uniform4f(loc.u_color, c.r, c.g, c.b, c.a);

    gl.drawArrays(gl.TRIANGLES, 0, this.geometryData.polygons.length / 2);
  }

  drawPoints(matrix) {
    const gl = this.gl;
    const program = this.programs.point;
    if (!program) return;

    const loc = this.locations.point;
    gl.useProgram(program);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.points);

    gl.enableVertexAttribArray(loc.a_position);
    gl.vertexAttribPointer(loc.a_position, 2, gl.FLOAT, false, 0, 0);

    gl.uniformMatrix4fv(loc.u_matrix, false, matrix);

    const c = this._colors.marker;
    gl.uniform4f(loc.u_color, c.r, c.g, c.b, c.a);

    const dpr = window.devicePixelRatio || 1;
    const zoom = this.map.getZoom();

    // Linear interpolation between a min and max size based on zoom.
    // This keeps markers readable when zoomed out and not oversized when zoomed in.
    const minSize = 24 * dpr;  // floor at low zoom
    const maxSize = 56 * dpr;  // ceiling at high zoom
    const minZoom = 4;
    const maxZoom = 14;
    const t = Math.max(0, Math.min(1, (zoom - minZoom) / (maxZoom - minZoom)));
    const pointSize = minSize + t * (maxSize - minSize);

    gl.uniform1f(loc.u_pointSize, pointSize);
    gl.uniform1f(loc.u_scale, 1.0);

    gl.drawArrays(gl.POINTS, 0, this.geometryData.points.length / 2);
  }

  // ── Ear-clipping triangulation helpers ─────────────────────────────

  isEar(points, indices, prev, curr, next, windingSign) {
    const a = points[prev];
    const b = points[curr];
    const c = points[next];

    const cross = (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
    // For CW winding (windingSign > 0 in y-down coords), convex ears have cross < 0.
    // For CCW winding (windingSign < 0), convex ears have cross > 0.
    if (windingSign > 0) {
      if (cross >= 0) return false;
    } else {
      if (cross <= 0) return false;
    }

    for (let i = 0; i < indices.length; i++) {
      const idx = indices[i];
      if (idx === prev || idx === curr || idx === next) continue;
      if (this.pointInTriangle(points[idx], a, b, c)) return false;
    }
    return true;
  }

  pointInTriangle(p, a, b, c) {
    const sign = (p1, p2, p3) =>
      (p1.x - p3.x) * (p2.y - p3.y) - (p2.x - p3.x) * (p1.y - p3.y);
    const d1 = sign(p, a, b);
    const d2 = sign(p, b, c);
    const d3 = sign(p, c, a);
    const hasNeg = (d1 < 0) || (d2 < 0) || (d3 < 0);
    const hasPos = (d1 > 0) || (d2 > 0) || (d3 > 0);
    return !(hasNeg && hasPos);
  }

  // ── GeoJSON parsing & bounds ───────────────────────────────────────

  parseGeoJson(geojson) {
    if (!geojson) return;
    this.features = geojson.type === 'FeatureCollection'
      ? geojson.features
      : [geojson];
    this.calculateBounds();
  }

  calculateBounds() {
    this.bounds = new google.maps.LatLngBounds();
    this.features.forEach(feature => {
      this.forEachLatLng(feature.geometry, (lat, lng) => {
        this.bounds.extend(new google.maps.LatLng(lat, lng));
      });
    });
  }

  forEachLatLng(geometry, callback) {
    const coords = geometry.coordinates;
    switch (geometry.type) {
      case 'Point':
        callback(coords[1], coords[0]);
        break;
      case 'MultiPoint':
      case 'LineString':
        coords.forEach(c => callback(c[1], c[0]));
        break;
      case 'MultiLineString':
      case 'Polygon':
        coords.forEach(ring => ring.forEach(c => callback(c[1], c[0])));
        break;
      case 'MultiPolygon':
        coords.forEach(poly => poly.forEach(ring => ring.forEach(c => callback(c[1], c[0]))));
        break;
    }
  }

  // ── Public API ─────────────────────────────────────────────────────

  getBounds() {
    return this.bounds;
  }

  setStyle(style) {
    this.style = { ...this.style, ...style };
    this._cacheColors();
    this.draw();
  }

  setVisible(visible) {
    this.style.visible = visible;
    if (visible) {
      this.draw();
    } else if (this.gl) {
      this.gl.clear(this.gl.COLOR_BUFFER_BIT);
    }
  }

  // ── Utility ────────────────────────────────────────────────────────

  hexToRgba(hex, alpha = 1) {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    return { r, g, b, a: alpha };
  }
}
